import { cn, getColorway } from 'utils/styles';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { CardsStackedItemBackwardsCompatibleProps } from 'libs/contentful/props';
import { ButtonWithIconProps } from '@1debit/chimekit/dist/server/Button/Button';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import Link from 'components/shared/Link/Link';
import BadgeOverlay from 'components/shared/BadgeOverlay/BadgeOverlay';
import { getIconByKey } from '@1debit/icons';
import BackwardsCompatibleText from 'components/shared/BackwardsCompatibleText/BackwardsCompatibleText';
import Media from 'components/shared/Media/Media';

interface Props extends CardsStackedItemBackwardsCompatibleProps, Pick<ButtonWithIconProps, 'iconProps'> {}

function CardsStackedItemImageFullBleedVariant({
  sys,
  sectionName,
  desktopMedia,
  desktopAspectRatio,
  mobileMedia,
  mobileAspectRatio,
  eyebrow,
  eyebrowRte,
  headline,
  shortDescription,
  cta,
  color,
  topRightBadge,
  desktopAnimation,
  mobileAnimation,
}: Props) {
  const cardWrapperClasses = cn(
    'relative',
    'rounded-lg active:rounded-lg min-h-[334px] sm:h-[570px] w-full',
    'flex flex-col justify-end md:basis-1/3-gap-6 group-data-[num-items=4]:md:basis-1/2-gap-6 md:grow-1',
    'hover:text-highlight active:text-highlight',
    getColorway(color),
  );

  const Icon = getIconByKey('arrow_right');

  const WrapperElement = !cta ? 'div' : Link;

  return (
    <WrapperElement
      {...(cta && { href: cta.url, internalPage: cta?.internalPage })}
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      className={cardWrapperClasses}
      data-testid="cardsStackedItemImageFullBleed">
      {desktopMedia && (
        <div className="rounded-lg">
          <Media
            media={{ ...desktopMedia, fill: true }}
            aspectRatio={desktopAspectRatio}
            mobileMedia={mobileMedia}
            mobileAspectRatio={mobileAspectRatio}
            sizes="(max-width: 1024px) 100vw, (max-width: 1440px) 50vw, 650px"
            desktopAnimation={desktopAnimation}
            mobileAnimation={mobileAnimation}
          />
        </div>
      )}
      {topRightBadge && (
        <BadgeOverlay
          description={topRightBadge.image.description}
          sys={topRightBadge.sys}
          url={topRightBadge.image.url}
          fileName={topRightBadge.image.fileName}
        />
      )}
      <div className={cn('relative z-10', 'w-full', 'p-6 sm:p-8 max-sm:pt-40')}>
        <CardContent
          sys={sys}
          eyebrow={<BackwardsCompatibleText text={eyebrow} richText={eyebrowRte} />}
          headline={headline}
          shortDescription={shortDescription}
        />
        {cta && (
          <div className="font-sans font-semibold flex items-center gap-2 mt-6">
            <span>{cta.text}</span>
            <Icon className="size-(--ck-icon-size)" />
          </div>
        )}
      </div>
    </WrapperElement>
  );
}

interface CardContentProps extends Pick<Props, 'sys' | 'headline' | 'shortDescription'> {
  eyebrow: string | React.ReactElement;
}

function CardContent({ sys, eyebrow, headline, shortDescription }: CardContentProps) {
  return (
    <div className="w-full flex flex-col gap-4">
      {eyebrow && (
        <span
          className="font-sans font-medium text-eyebrow"
          {...liveUpdateAttrs(sys?.id, 'eyebrow')}
          data-testid="eyebrow">
          {eyebrow}
        </span>
      )}
      {headline && (
        <div
          className="text-heading-md font-sans font-semibold"
          {...liveUpdateAttrs(sys?.id, 'headline')}
          data-testid="headline">
          <RichTextHeadline headline={headline} level="h3" />
        </div>
      )}
      {shortDescription && (
        <div
          className="font-serif text-lg"
          {...liveUpdateAttrs(sys?.id, 'shortDescription')}
          data-testid="shortDescription">
          <RichTextCopy copy={shortDescription} ySpacing="none" />
        </div>
      )}
    </div>
  );
}

export default CardsStackedItemImageFullBleedVariant;
